import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { usePageSEO } from "../lib/seo";
import { Button } from "../components/ui/button";
import { Loader2, ArrowRight, LogOut, BarChart3, Target, Clock, Shield, TrendingUp, Calendar, Download, Presentation, CheckCircle, Rocket, Plus, Users, ChevronRight } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { motion } from "framer-motion";

export default function UserDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  usePageSEO({ title: "Dashboard", noIndex: true });
  const [audits, setAudits] = useState([]);
  const [latestAudit, setLatestAudit] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [teamAudits, setTeamAudits] = useState([]);
  const [team, setTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [downloadingDeck, setDownloadingDeck] = useState(false);
  const [showTeam, setShowTeam] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const { data: auditList } = await api.get("/audit/user/list");
        if (auditList.length === 0) { navigate("/blueprint"); return; }
        const inProgress = auditList.find(a => a.status === "in_progress");
        if (inProgress && auditList.length === 1) { navigate("/blueprint"); return; }
        const completed = auditList.filter(a => a.status !== "in_progress");
        setAudits(completed);
        // Get latest completed paid audit with full data
        const latest = completed.find(a => a.payment_status === "paid");
        if (latest) {
          const { data: full } = await api.get(`/audit/${latest.audit_id}`);
          setLatestAudit({ ...latest, ...full, audit_id: latest.audit_id });
        } else if (completed.length > 0) {
          navigate(`/score-preview/${completed[0].audit_id}`);
          return;
        }
        // Load team + team audits
        try {
          const { data: teamData } = await api.get("/team");
          if (teamData.team) {
            setTeam(teamData.team);
            const { data: ta } = await api.get("/team/audits");
            setTeamAudits(ta.filter(a => a.member_email !== user?.email));
          }
        } catch (e) { console.error("Team load failed:", e); }
        try { const { data: b } = await api.get("/booking/user"); setBookings(b); } catch (e) { console.error("Bookings load failed:", e); }
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const handleLogout = async () => { await logout(); navigate("/"); };

  const downloadFile = async (type, auditId) => {
    const setter = type === "pdf" ? setDownloading : setDownloadingDeck;
    setter(true);
    try {
      const endpoint = type === "pdf" ? `/report/${auditId}/pdf` : `/report/${auditId}/deck`;
      const response = await api.get(endpoint, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      const name = (latestAudit?.company_info?.company_name || "Report").replace(/\s/g, "_");
      link.setAttribute("download", type === "pdf" ? `Stansbury_AI_Report_${name}.pdf` : `Stansbury_AI_Exec_Deck_${name}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) { console.error(err); }
    finally { setter(false); }
  };

  if (loading) return <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#0033CC]" /></div>;
  if (!latestAudit) return null;

  const scores = latestAudit.scores || {};
  const sections = scores.sections || {};
  const report = latestAudit.report || {};
  const company = latestAudit.company_info || {};
  const tierColors = { "Reactive": "#EF4444", "Emerging": "#F59E0B", "Operationalizing": "#818CF8", "Systemized": "#10B981", "AI-Native": "#0033CC" };
  const sectionIcons = { acquire: Target, convert: TrendingUp, operate: BarChart3, expand: Rocket, defend: Shield, readiness: Clock };
  const hasReport = !!report.executive_summary;
  const hasBooking = bookings.length > 0;
  const weakest = Object.entries(sections).sort((a, b) => (a[1].percentage || 0) - (b[1].percentage || 0))[0];
  const strongest = Object.entries(sections).sort((a, b) => (b[1].percentage || 0) - (a[1].percentage || 0))[0];

  // Build score history for chart
  const completedWithScores = audits.filter(a => a.scores?.overall && a.payment_status === "paid");
  const historyData = [...completedWithScores].reverse().map((a, i) => ({
    name: a.created_at ? new Date(a.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : `Audit ${i + 1}`,
    score: a.scores.overall,
    audit_id: a.audit_id,
  }));

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="user-dashboard">
      <header className="backdrop-blur-2xl bg-white/80 border-b border-slate-200/60 sticky top-0 z-50 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto flex items-center justify-between h-16">
          <img src="/stansbury-logo.png" alt="Stansbury AI" className="h-10 sm:h-12 flex-shrink-0" />
          <div className="flex items-center gap-3">
            {team && (
              <button onClick={() => setShowTeam(!showTeam)} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#0033CC] font-['Outfit'] transition-colors" data-testid="team-toggle">
                <Users className="w-3.5 h-3.5" />{team.name}
              </button>
            )}
            {user?.role === "admin" && (
              <Button variant="ghost" onClick={() => navigate("/ops")} className="text-sm text-slate-500 font-['Outfit']" data-testid="dashboard-admin-link">OPS</Button>
            )}
            <button onClick={handleLogout} className="text-slate-400 hover:text-slate-600" data-testid="dashboard-logout-btn"><LogOut className="w-4 h-4" /></button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 md:px-12 py-8">
        {/* Welcome */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <p className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-[#0033CC] mb-2">Your AI Growth Co-Pilot</p>
          <h1 className="text-2xl sm:text-3xl font-['Outfit'] font-medium tracking-tight text-[#0F172A] mb-1">
            Welcome back, {user?.name || company.company_name || "there"}
          </h1>
          <p className="text-sm text-slate-500 font-['IBM_Plex_Sans']">{company.company_name} &middot; {(company.industry || "").replace(/_/g, " ")} &middot; {company.company_size} employees</p>
        </motion.div>

        {/* Score + Key Metrics */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-8">
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
            className="lg:col-span-1 bg-white border border-slate-200 p-6 cursor-pointer hover:border-slate-300 transition-colors"
            onClick={() => navigate(`/results/${latestAudit.audit_id}`)} data-testid="score-card">
            <p className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.15em] text-slate-400 mb-3">Latest Score</p>
            <div className="text-5xl font-['Outfit'] font-light text-[#0033CC] mb-1">{scores.overall || 0}</div>
            <p className="text-sm text-slate-500 font-['IBM_Plex_Sans']">/100</p>
            <div className="flex items-center gap-2 mt-3">
              <div className="w-2.5 h-2.5" style={{ backgroundColor: tierColors[scores.tier] || "#818CF8" }} />
              <span className="text-sm font-['Outfit'] font-medium text-[#0F172A]">{scores.tier}</span>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="lg:col-span-3 grid grid-cols-3 gap-4">
            <div className="bg-white border border-slate-200 p-5">
              <p className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.15em] text-slate-400 mb-2">Revenue Leak</p>
              <div className="text-2xl font-['Outfit'] font-medium text-red-600">{scores.revenue_leak_severity || "-"}</div>
              <p className="text-xs text-slate-400 font-['IBM_Plex_Sans'] mt-1">{scores.revenue_opportunity || 0}% opportunity</p>
            </div>
            <div className="bg-white border border-slate-200 p-5">
              <p className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.15em] text-slate-400 mb-2">Biggest Gap</p>
              <div className="text-2xl font-['Outfit'] font-medium text-[#0F172A] capitalize">{weakest?.[0] || "-"}</div>
              <p className="text-xs text-slate-400 font-['IBM_Plex_Sans'] mt-1">{weakest?.[1]?.percentage || 0}% — highest priority</p>
            </div>
            <div className="bg-white border border-slate-200 p-5">
              <p className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.15em] text-slate-400 mb-2">Strongest</p>
              <div className="text-2xl font-['Outfit'] font-medium text-emerald-600 capitalize">{strongest?.[0] || "-"}</div>
              <p className="text-xs text-slate-400 font-['IBM_Plex_Sans'] mt-1">{strongest?.[1]?.percentage || 0}% — build on this</p>
            </div>
          </motion.div>
        </div>

        {/* Category Scores */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
          className="bg-white border border-slate-200 p-6 mb-8 cursor-pointer hover:border-slate-300 transition-colors"
          onClick={() => navigate(`/results/${latestAudit.audit_id}`)} data-testid="category-scores">
          <p className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.15em] text-[#0033CC] mb-4">AI Growth Dimensions</p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {Object.entries(sections).map(([key, val]) => {
              const Icon = sectionIcons[key] || BarChart3;
              const pct = val.percentage || 0;
              const color = pct >= 70 ? "#059669" : pct >= 40 ? "#F59E0B" : "#DC2626";
              return (
                <div key={key} className="text-center">
                  <div className="w-10 h-10 mx-auto mb-2 flex items-center justify-center" style={{ backgroundColor: color + "15" }}>
                    <Icon className="w-5 h-5" style={{ color }} strokeWidth={1.5} />
                  </div>
                  <div className="text-lg font-['Outfit'] font-medium text-[#0F172A]">{pct}%</div>
                  <p className="text-xs text-slate-500 font-['IBM_Plex_Sans'] capitalize">{key}</p>
                </div>
              );
            })}
          </div>
        </motion.div>

        {/* Score History Chart (only if > 1 completed audit) */}
        {historyData.length > 1 && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.18 }}
            className="bg-white border border-slate-200 p-6 mb-8" data-testid="score-history-chart">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.15em] text-[#0033CC] mb-1">Score History</p>
                <p className="text-xs text-slate-400 font-['IBM_Plex_Sans']">{historyData.length} blueprints completed</p>
              </div>
              {historyData.length >= 2 && (() => {
                const delta = historyData[historyData.length - 1].score - historyData[0].score;
                return (
                  <div className={`text-sm font-['JetBrains_Mono'] ${delta >= 0 ? "text-emerald-600" : "text-red-500"}`}>
                    {delta >= 0 ? "+" : ""}{delta} pts
                  </div>
                );
              })()}
            </div>
            <ResponsiveContainer width="100%" height={180}>
              <LineChart data={historyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: "#94A3B8" }} axisLine={false} tickLine={false} width={30} />
                <Tooltip contentStyle={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 12, border: "1px solid #E2E8F0", borderRadius: 0 }} />
                <Line type="monotone" dataKey="score" stroke="#0033CC" strokeWidth={2} dot={{ fill: "#0033CC", r: 4 }} activeDot={{ r: 6 }} />
              </LineChart>
            </ResponsiveContainer>
          </motion.div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Left: AI Insights + Opportunities */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="lg:col-span-2 space-y-4">
            {hasReport && (
              <div className="bg-[#0F172A] p-6" data-testid="ai-insight">
                <p className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-[#818CF8] mb-3">AI Insight</p>
                <p className="text-sm text-slate-200 font-['IBM_Plex_Sans'] leading-relaxed mb-4">
                  {report.executive_summary?.substring(0, 300)}{report.executive_summary?.length > 300 ? "..." : ""}
                </p>
                <Button onClick={() => navigate(`/results/${latestAudit.audit_id}`)} variant="ghost" className="text-[#818CF8] hover:text-white font-['Outfit'] text-sm p-0 h-auto" data-testid="view-full-report">
                  View Full Report <ArrowRight className="w-3.5 h-3.5 ml-1" />
                </Button>
              </div>
            )}
            {report.opportunities && (
              <div className="bg-white border border-slate-200 p-6" data-testid="top-opportunities">
                <p className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.15em] text-[#0033CC] mb-3">Your Top Opportunities</p>
                <div className="space-y-3">
                  {(report.opportunities || []).slice(0, 3).map((o, i) => {
                    const isObj = typeof o === "object";
                    const title = isObj ? o.title : o;
                    const impact = isObj ? o.impact : "";
                    const value = isObj ? (o.revenue_impact || o.estimated_value || "") : "";
                    return (
                      <div key={`idx-${i}`} className="flex items-start gap-3 pb-3 border-b border-slate-100 last:border-0 last:pb-0">
                        <div className="w-6 h-6 bg-[#0033CC] text-white flex items-center justify-center text-xs font-['JetBrains_Mono'] flex-shrink-0 mt-0.5">{i + 1}</div>
                        <div className="flex-1">
                          <p className="text-sm font-['Outfit'] font-medium text-[#0F172A]">{title}</p>
                          {isObj && o.description && <p className="text-xs text-slate-500 font-['IBM_Plex_Sans'] mt-0.5 leading-relaxed">{o.description.substring(0, 120)}</p>}
                        </div>
                        <div className="flex-shrink-0 text-right">
                          {impact && <span className={`text-[10px] font-['JetBrains_Mono'] px-1.5 py-0.5 ${impact === "High" ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-700"}`}>{impact}</span>}
                          {value && <p className="text-xs font-['JetBrains_Mono'] text-emerald-600 mt-0.5">{value}</p>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </motion.div>

          {/* Right Sidebar */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }} className="space-y-4">
            {/* Downloads */}
            <div className="bg-white border border-slate-200 p-5" data-testid="downloads">
              <p className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.15em] text-[#0033CC] mb-3">Your Reports</p>
              <div className="space-y-2">
                <Button onClick={() => downloadFile("pdf", latestAudit.audit_id)} disabled={downloading} className="w-full bg-[#0033CC] text-white hover:bg-[#002299] rounded-none h-10 font-['Outfit'] text-sm justify-start" data-testid="dl-report-pdf">
                  {downloading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}Growth Blueprint Report
                </Button>
                <Button onClick={() => downloadFile("deck", latestAudit.audit_id)} disabled={downloadingDeck} variant="outline" className="w-full border-[#0033CC] text-[#0033CC] hover:bg-[#0033CC] hover:text-white rounded-none h-10 font-['Outfit'] text-sm justify-start transition-colors" data-testid="dl-exec-deck">
                  {downloadingDeck ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Presentation className="w-4 h-4 mr-2" />}Executive Deck
                </Button>
              </div>
            </div>

            {/* Book a Call */}
            {!hasBooking ? (
              <div className="bg-white border border-[#0033CC]/20 p-5" data-testid="book-call-widget">
                <p className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.15em] text-[#0033CC] mb-2">Recommended</p>
                <h3 className="text-base font-['Outfit'] font-medium text-[#0F172A] mb-2">Book Your Strategy Call</h3>
                <p className="text-xs text-slate-500 font-['IBM_Plex_Sans'] leading-relaxed mb-3">Walk through your results with a growth strategist and get a custom implementation plan.</p>
                <Button onClick={() => navigate(`/booking/${latestAudit.audit_id}`)} className="w-full bg-[#0033CC] text-white hover:bg-[#002299] rounded-none h-10 font-['Outfit'] text-sm">
                  <Calendar className="w-4 h-4 mr-2" />Schedule Call
                </Button>
              </div>
            ) : (
              <div className="bg-emerald-50 border border-emerald-200 p-5" data-testid="booking-confirmed">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="w-4 h-4 text-emerald-600" />
                  <p className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.15em] text-emerald-700">Call Scheduled</p>
                </div>
                <p className="text-sm font-['IBM_Plex_Sans'] text-[#0F172A]">{bookings[0]?.date} at {bookings[0]?.time_slot} EST</p>
                <p className="text-xs text-slate-500 font-['IBM_Plex_Sans'] mt-1">We'll review your blueprint findings together.</p>
              </div>
            )}

            {/* Start New Blueprint */}
            <div className="bg-white border border-dashed border-slate-300 p-5 text-center" data-testid="start-new-blueprint">
              <Plus className="w-5 h-5 text-slate-400 mx-auto mb-2" />
              <p className="text-sm font-['Outfit'] font-medium text-[#0F172A] mb-1">Run Another Blueprint</p>
              <p className="text-xs text-slate-400 font-['IBM_Plex_Sans'] mb-3">Track progress over time</p>
              <Button onClick={() => navigate("/blueprint")} variant="outline" className="rounded-none h-8 text-xs font-['Outfit'] border-slate-300">
                Start New Blueprint
              </Button>
            </div>
          </motion.div>
        </div>

        {/* Audit History */}
        {completedWithScores.length > 1 && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.28 }}
            className="bg-white border border-slate-200 p-6 mb-8" data-testid="audit-history">
            <p className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.15em] text-[#0033CC] mb-4">Blueprint History</p>
            <div className="space-y-2">
              {completedWithScores.map((a, i) => {
                const isLatest = a.audit_id === latestAudit.audit_id;
                const score = a.scores?.overall || 0;
                const tier = a.scores?.tier || "";
                const date = a.created_at ? new Date(a.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "";
                return (
                  <div key={a.audit_id}
                    onClick={() => navigate(`/results/${a.audit_id}`)}
                    className={`flex items-center gap-4 p-3 cursor-pointer transition-colors hover:bg-slate-50 ${isLatest ? "border border-[#0033CC]/20 bg-[#0033CC]/[0.02]" : "border border-slate-100"}`}
                    data-testid={`audit-history-item-${i}`}>
                    <div className="w-12 h-12 flex items-center justify-center bg-slate-50">
                      <span className="text-lg font-['Outfit'] font-light text-[#0033CC]">{score}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-['Outfit'] font-medium text-[#0F172A] truncate">{a.company_info?.company_name || "Blueprint"}</p>
                        {isLatest && <span className="text-[10px] font-['JetBrains_Mono'] bg-[#0033CC] text-white px-1.5 py-0.5">LATEST</span>}
                        {a.report_tier === "white_glove" && <span className="text-[10px] font-['JetBrains_Mono'] bg-[#0F172A] text-white px-1.5 py-0.5">WHITE GLOVE</span>}
                      </div>
                      <p className="text-xs text-slate-400 font-['IBM_Plex_Sans']">{date} &middot; {tier}</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-300" />
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* Team Audits */}
        {showTeam && team && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            className="bg-white border border-slate-200 p-6 mb-8" data-testid="team-audits-section">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.15em] text-[#0033CC] mb-1">{team.name} Team</p>
                <p className="text-xs text-slate-400 font-['IBM_Plex_Sans']">{team.members?.length || 0} members &middot; {teamAudits.length} blueprints</p>
              </div>
              <Button onClick={() => navigate("/team")} variant="outline" className="rounded-none h-8 text-xs font-['Outfit'] border-slate-300" data-testid="manage-team-btn">
                <Users className="w-3 h-3 mr-1.5" />Manage Team
              </Button>
            </div>
            {teamAudits.length > 0 ? (
              <div className="space-y-2">
                {teamAudits.slice(0, 5).map((a, i) => (
                  <div key={`ta-${i}`} className="flex items-center gap-4 p-3 border border-slate-100 hover:bg-slate-50 transition-colors cursor-pointer"
                    onClick={() => a.has_report && a.payment_status === "paid" ? navigate(`/results/${a.audit_id}`) : null}
                    data-testid={`team-audit-${i}`}>
                    <div className="w-10 h-10 flex items-center justify-center bg-slate-50">
                      <span className="text-base font-['Outfit'] font-light text-[#0033CC]">{a.scores?.overall || "-"}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-['Outfit'] font-medium text-[#0F172A] truncate">{a.company_info?.company_name || "Blueprint"}</p>
                      <p className="text-xs text-slate-400 font-['IBM_Plex_Sans']">{a.member_name || a.member_email} &middot; {a.created_at ? new Date(a.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : ""}</p>
                    </div>
                    {a.has_report && <ChevronRight className="w-4 h-4 text-slate-300" />}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-slate-400 text-center py-4 font-['IBM_Plex_Sans']">No team blueprints yet. Invite members to get started.</p>
            )}
          </motion.div>
        )}

        {/* Roadmap Preview */}
        {report.roadmap && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
            className="bg-white border border-slate-200 p-6 mb-8" data-testid="roadmap-preview">
            <p className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.15em] text-[#0033CC] mb-4">Your Execution Roadmap</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { key: "days_0_30", label: "0-30 Days", sub: "Quick Wins", color: "#059669" },
                { key: "days_30_90", label: "30-90 Days", sub: "Foundation", color: "#0033CC" },
                { key: "days_90_plus", label: "90+ Days", sub: "Scale", color: "#7C3AED" },
              ].map(({ key, label, sub, color }) => {
                const items = report.roadmap[key] || [];
                return items.length > 0 && (
                  <div key={key} className="border border-slate-200 border-t-2 p-4" style={{ borderTopColor: color }}>
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-xs font-['JetBrains_Mono'] uppercase tracking-wider" style={{ color }}>{label}</span>
                      <span className="text-[10px] text-slate-400 font-['IBM_Plex_Sans']">{sub}</span>
                    </div>
                    <ul className="space-y-1.5">
                      {items.slice(0, 3).map((item, j) => (
                        <li key={j} className="text-xs text-slate-600 font-['IBM_Plex_Sans'] flex items-start gap-1.5">
                          <span style={{ color }} className="mt-0.5">+</span>{item}
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
